import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/Layout/MainLayout';
import { BackIcon } from '../../../components/Dashboard/DataCenterIcons';
import { API_CONFIG, ROUTES } from '../../../utils/constants';
import '../../../assets/styles/RegularRDCPage.css';
import commonService from '../../../services/commonService';

// ---------- Reusable Multi-Select Input ----------
interface MultiSelectSearchInputProps {
  label: string;
  field: string;
  selectedValues: string[];
  onChange: (field: string, values: string[]) => void;
  fetchSuggestions: (field: string, query: string, dataType: string) => Promise<string[]>;
  getDefaultSuggestions: (field: string) => Promise<string[]>;
  dataType: string;
}

const MultiSelectSearchInput: React.FC<MultiSelectSearchInputProps> = ({
  label,
  field,
  selectedValues,
  onChange,
  fetchSuggestions,
  getDefaultSuggestions,
  dataType,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [cachedDefaults, setCachedDefaults] = useState<{ [key: string]: string[] }>({});

  // ✅ Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSuggestions([]); // clear dropdown values
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ✅ Debounced API Call for typed input
  useEffect(() => {
    const delay = setTimeout(async () => {
      if (inputValue.trim()) {
        try {
          setLoading(true);
          const data = await fetchSuggestions(field, inputValue, dataType);
          if (field === 'zipcode' || field === 'naics_code' || field === 'sic_code') {
            setSuggestions([...new Set([inputValue, ...data])]); // Include input
          } else {
            setSuggestions([...new Set(data)]); // exclude input
          }
          setShowDropdown(true);
        } catch (err) {
          console.error('Error fetching suggestions:', err);
        } finally {
          setLoading(false);
        }
      } else {
        setShowDropdown(false);
        setSuggestions([]);
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [inputValue, dataType, field, fetchSuggestions]);

  // ✅ Fetch default suggestions on focus (if input empty)
  const handleFocus = async () => {
    if (!inputValue.trim()) {
      if (!cachedDefaults[field]) {
        try {
          setLoading(true);
          const data = await getDefaultSuggestions(field);
          setCachedDefaults((prev) => ({ ...prev, [field]: data }));
          setSuggestions(data);
        } catch (err) {
          console.error('Error fetching default suggestions:', err);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions(cachedDefaults[field]);
      }
      setShowDropdown(true);
    } else {
      setShowDropdown(true);
    }
  };

  const addValue = (val: string) => {
    if (!selectedValues.includes(val)) {
      onChange(field, [...selectedValues, val]);
    }
    setInputValue('');
    setShowDropdown(false);
    setSuggestions([]);
  };

  const removeValue = (val: string) => {
    onChange(field, selectedValues.filter((item) => item !== val));
  };

  return (
    <div className="regular-rdc-input-wrapper" ref={wrapperRef}>
      {/* <label className="regular-rdc-label">{label}</label> */}

      <div className="regular-rdc-multiselect">
        <div className="regular-rdc-tags">
          {selectedValues.map((val) => (
            <span key={val} className="regular-rdc-tag">
              {val}
              <button className="regular-rdc-close-btn" onClick={() => removeValue(val)}>
                ✕
              </button>
            </span>
          ))}
          <input
            type="text"
            placeholder={`Search ${label}`}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={handleFocus}
            className="regular-rdc-input"
          />
        </div>

        {showDropdown && (
          <ul className="regular-rdc-dropdown" style={{ maxHeight: "150px" }}>
            {loading ? (
              <li className="loading">Loading...</li>
            ) : suggestions.length > 0 ? (
              suggestions.map((s, idx) => (
                <li key={idx} onClick={() => addValue(s)}>
                  {s}
                </li>
              ))
            ) : (
              <li className="no-results">No results</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------- Main Page ----------
const RegularRDCPage: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<any>({
    data_type: 'b2b',
    industry: [],
    job_title: [],
    job_level_name: [],
    employee_size_range: [],
    revenue_range: [],
    sic_code: [],
    naics_code: [],
    city: [],
    zipcode: [],
    state: [],
    country: [],
    technology_name: [],
    company_name: [],
    company_website: [],
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleFilterChange = (field: string, values: any) => {
    setFilters((prev: any) => ({
      ...prev,
      [field]: values,
    }));
  };

  // ✅ Default suggestions fetcher
  const getDefaultSuggestions = async (field: string): Promise<string[]> => {
    const response = await commonService.getAll(field + '_default' as keyof typeof API_CONFIG.ENDPOINTS);
    const items = response.data || [];
    const results = items
      .map((item: any) => item[field])
      .filter((value: string | undefined) => !!value);
    return results;
  };

  // ✅ Search suggestions fetcher
  const fetchSuggestions = async (field: string, query: string, dataType: string): Promise<string[]> => {
    console.log(`Fetching suggestions for field: ${field}, query: ${query}, type: ${dataType}`);
    const response = await commonService.search(field as keyof typeof API_CONFIG.ENDPOINTS, query);
    const items = response.data || [];
    const results = items
      .map((item: any) => item[field])
      .filter((value: string | undefined) => !!value);
    return results;
  };

  const handleToggle = () => {
    setFilters((prev: any) => ({
      ...prev,
      data_type: prev.data_type === 'b2b' ? 'b2c' : 'b2b',
    }));
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      // console.log('Search filters:', filters);
      // const results = '';
      const results = await commonService.rdcSearch(filters);
      navigate(ROUTES.RDC_COUNT, {
        state: {
          filters,
          results,
        },
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="regular-rdc-page custom-scrollbar">
        <main className="regular-rdc-main">
          <div className="regular-rdc-section-header">
            <button onClick={() => navigate(ROUTES.RDC)} className="regular-rdc-back-button">
              <BackIcon /> Back
            </button>
          </div>
          <div className='rdc-custom-download-card' style={{ backgroundColor: "#ffffff00" }}>
            <h3 className="regular-rdc-filters-title">Search by Filters</h3>

            <div className="regular-rdc-filter-grid">
              {[
                { field: 'industry', label: 'Industry' },
                { field: 'job_title', label: 'Job Title' },
                { field: 'job_level_name', label: 'Job Title Level' },
                { field: 'employee_size_range', label: 'Employee Size' },
                { field: 'revenue_range', label: 'Revenue Size' },
                { field: 'sic_code', label: 'SIC' },
                { field: 'naics_code', label: 'NAICS' },
                { field: 'city', label: 'City' },
                { field: 'zipcode', label: 'Zip Code' },
                { field: 'state', label: 'State' },
                { field: 'country', label: 'Country' },
                { field: 'technology_name', label: 'Technology' },
                { field: 'company_name', label: 'Company Names' },
                { field: 'company_website', label: 'Company Website' },
              ].map((item) => (
                <MultiSelectSearchInput
                  key={item.field}
                  label={item.label}
                  field={item.field}
                  selectedValues={filters[item.field] || []}
                  onChange={handleFilterChange}
                  fetchSuggestions={fetchSuggestions}
                  getDefaultSuggestions={getDefaultSuggestions}
                  dataType={filters.data_type}
                />
              ))}
            </div>

            {/* B2B/B2C Toggle */}
            <div className="regular-rdc-toggle-section">
              <label className="regular-rdc-toggle-label">
                <span className="regular-rdc-toggle-text">B2B</span>
                <div
                  onClick={handleToggle}
                  className={`regular-rdc-toggle ${filters.data_type === 'b2c' ? 'active' : 'inactive'}`}
                >
                  <div className="regular-rdc-toggle-knob" />
                </div>
                <span className="regular-rdc-toggle-text">B2C</span>
              </label>
            </div>

            <div className="regular-rdc-search-button-container">
              <button onClick={handleSearch} disabled={isLoading} className="regular-rdc-search-button">
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
          <button
            onClick={() => navigate(ROUTES.RDC_STATUS)}
            className="rde-status-button mt-4 " style={{padding:"5px 0px"}}
          >
            Check Status of Your Download
          </button>
        </main>
      </div>
    </MainLayout>
  );
};

export default RegularRDCPage;
